import { useHandleBack } from "helpers/functions/useHandleBack";
import React, { useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchLatestItemById } from "slices/item/thunk";
import {
  fetchTemplateById,
  updateTemplate,
  updateTemplateWithLatestItems,
} from "slices/template/thunk";

// -----------------------------
// Type Definitions
// -----------------------------
interface ItemAttributes {
  brand?: string;
  size?: string;
  type?: string;
  model?: string;
  wattage?: string;
}

interface Item {
  attributes: ItemAttributes;
  category: string;
  category_name: string;
  gst: string;
  tax_rate?: number;
  discount?: number;
  discount_type?: "%" | "flat" | string;
  taxable_value?: number;
  tax_amount?: number;
  item_amount?: number;
  hsn_code: string | null;
  hsn_name: string | null;
  image: string | null;
  item_id: string;
  item_name: string;
  item_sku: string;
  quantity: number;
  remarks: string;
  selling_price: string;
  tag_name: string | null;
  tags: string | null;
}

// interface Template {
//   description: string;
//   item_details: Record<string, Item[]>;
//   keywords: string[];
//   template_name: string;
// }

const TemplateView: React.FC = () => {
  const handleBack = useHandleBack("/template");
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();

  const { selectedTemplate, loading } = useSelector(
    (state: any) => state.TemplateReducer
  );

  const calculateTemplateItemValues = (item: any) => {
    const quantity = Math.max(1, Math.min(10000, Math.floor(Number(item.quantity) || 1)));
    const sellingPrice = Number(item.selling_price || 0);
    const discount = Number(item.discount || 0);
    const discountType = String(item.discount_type || "%").trim();

    const totalSellingPrice = sellingPrice * quantity;

    let totalDiscount = 0;
    if (discountType === "%") {
      totalDiscount = (discount / 100) * totalSellingPrice;
    } else {
      totalDiscount = discount;
    }

    totalDiscount = Math.max(0, Math.min(totalDiscount, totalSellingPrice));

    const taxableValue = Number((totalSellingPrice - totalDiscount).toFixed(2));
    const taxAmount = 0;
    const itemAmount = Number((taxableValue + taxAmount).toFixed(2));

    return {
      ...item,
      quantity,
      tax_rate: Number(item.gst ?? item.tax_rate ?? 0),
      gst: Number(item.gst ?? item.tax_rate ?? 0),
      taxable_value: taxableValue,
      tax_amount: taxAmount,
      item_amount: itemAmount,
    };
  };

  const roundUsingFirstDecimal = (value: number) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;

    const absNum = Math.abs(num);
    const normalized = absNum.toFixed(2);
    const parts = normalized.split(".");
    const integerPart = Number(parts[0] || "0");
    const firstDecimalDigit = Number((parts[1] || "0")[0] || "0");

    let rounded = integerPart;
    if (firstDecimalDigit >= 5) rounded += 1;

    return num < 0 ? -rounded : rounded;
  };

  // Flatten item_details into a single array
  const items = selectedTemplate?.item_details
    ? Object.values(selectedTemplate.item_details).flat()
    : [];

  // console.log(items);
  // useEffect(() => {
  //   if (id) {
  //     dispatch(fetchTemplateById(id));
  //   }
  // }, [id, dispatch]);

  // Fetch Template with latest Item values
  useEffect(() => {
    if (!id) return;

    const fetchTemplateAndLatestItems = async () => {
      try {
        // Fetch the template first
        const templateData: any = await dispatch(
          fetchTemplateById(id)
        ).unwrap();

        // Flatten item_details for easier processing
        const itemsToProcess: Item[] = [];
        Object.entries(templateData.item_details).forEach(
          ([categoryName, items]) => {
            (items as Item[]).forEach((item) => {
              itemsToProcess.push({ ...item, category_name: categoryName });
            });
          }
        );

        console.log(itemsToProcess);

        // Fetch latest data for each item in parallel
        const updatedItems = await Promise.all(
          itemsToProcess.map(async (item) => {
            try {
              const latestItem: any = await dispatch(
                fetchLatestItemById(item.item_id)
              ).unwrap();

              console.log(latestItem);
              const mergedItem = { ...item, ...latestItem, quantity: item.quantity };
              return calculateTemplateItemValues({
                ...mergedItem,
                quantity: mergedItem.quantity || 1,
                tax_rate: Number(mergedItem.gst ?? mergedItem.tax_rate ?? 0),
                gst: Number(mergedItem.gst ?? mergedItem.tax_rate ?? 0),
                discount: mergedItem.discount ?? 0,
                discount_type: mergedItem.discount_type ?? "%",
              }); // merge latest values + recalculate totals with decimals
            } catch (err) {
              console.warn(`Failed to fetch latest item ${item.item_id}`, err);
              return calculateTemplateItemValues({
                ...item,
                quantity: item.quantity || 1,
                tax_rate: Number(item.gst ?? item.tax_rate ?? 0),
                gst: Number(item.gst ?? item.tax_rate ?? 0),
                discount: item.discount ?? 0,
                discount_type: item.discount_type ?? "%",
              }); // fallback to original + recalculate totals with decimals
            }
          })
        );

        console.log(updatedItems);

        // Rebuild item_details grouped by category_name
        const updatedItemDetails: Record<string, Item[]> = {};
        updatedItems.forEach((item) => {
          if (!updatedItemDetails[item.category_name])
            updatedItemDetails[item.category_name] = [];
          updatedItemDetails[item.category_name].push(item);
        });
        
        const hasItemDetailsChanged =
          JSON.stringify(templateData.item_details || {}) !==
          JSON.stringify(updatedItemDetails);

        if (hasItemDetailsChanged) {
          try {
            await dispatch(
              updateTemplate({
                template_id: templateData.template_id || id,
                template_name: templateData.template_name,
                description: templateData.description || "",
                keywords: templateData.keywords || [],
                item_details: updatedItemDetails,
              })
            ).unwrap();
          } catch (persistErr) {
            console.warn("Failed to persist latest template item values", persistErr);
          }
        }

        // Update Redux state or local state
        dispatch(
          updateTemplateWithLatestItems({
            ...templateData,
            item_details: updatedItemDetails,
          })
        );
      } catch (err) {
        console.error("Failed to fetch template or latest items", err);
      }
    };

    fetchTemplateAndLatestItems();
  }, [id, dispatch]);

  return (
    <Container fluid className="page-content">
      {/* Template Details */}
      <Card className="p-1 mb-4 shadow-sm rounded-3">
        <Card.Header className="mb-3">
          <Row className="align-items-center g-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
              {/* Header */}
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{ fontSize: "24px", cursor: "pointer" }}
                  onClick={handleBack}
                >
                  ←
                </span>
                <h5 className="mb-0 fw-bold">View Template</h5>
              </div>

              {/* Right: Breadcrumb */}
              <div className="text-muted">
                <span
                  onClick={handleBack}
                  style={{ color: "#6c757d", cursor: "default" }}
                >
                  Template
                </span>{" "}
                &gt;{" "}
                <span style={{ color: "#6c757d", cursor: "default" }}>
                  View Template
                </span>
              </div>
            </div>
          </Row>
        </Card.Header>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Card.Body className="py-0">
            <h5 className="mb-3 px-2 text-secondary fw-bold">
              Template Details
            </h5>
            <Row className="gy-2 px-2">
              <Col xs={12} xl={6}>
                <strong>Template Name :</strong>{" "}
                {selectedTemplate?.template_name}
              </Col>
              <Col xs={12} xl={6}>
                <strong>Keywords :</strong>{" "}
                {selectedTemplate?.keywords.join(", ")}
              </Col>
              <Col md={12}>
                <strong>Description :</strong>{" "}
                {selectedTemplate?.description || "-"}
              </Col>
            </Row>

            <hr />
            {/* Items Table */}

            <h5 className="mb-3 p-2 text-secondary fw-bold">Items Details</h5>
            <div className="px-2">
              <Table striped bordered hover responsive className="align-middle">
                <thead className="table-primary text-center">
                  <tr>
                    <th>Category</th>
                    <th>Item Name</th>
                    <th>Brand</th>
                    <th>Item SKU</th>
                    <th>Quantity</th>
                    <th>Selling Price (₹)</th>
                    <th>Discount</th>
                    <th>Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: any, index) => {
                    const price = parseFloat(item.selling_price || "0");
                    const qty = Number(item.quantity) || 0;
                    const roundedTotal = roundUsingFirstDecimal(
                      Number(item.item_amount || 0)
                    );

                    const brandName = item.brand_name ||
                       item.attributes.find(
                          (attr: any) =>
                            attr.attribute_name === "Brand" ||
                            attr.attribute_name === "brand"
                        )?.manage_value_name || "-";

                    return (
                      <tr key={index}>
                        <td>{item.category_name}</td>
                        <td>{item.item_name}</td>
                        <td>{brandName}</td>
                        <td>{item.item_sku}</td>
                        <td className="text-center">{qty}</td>
                        <td className="text-end">
                          {price.toLocaleString("en-IN")}
                        </td>
                        <td className="text-center">
                          {Number(item.discount) === 0
                            ? "-"
                            : `${item.discount} ${
                                item.discount_type === "flat" ? "₹" : "%"
                              }`}
                        </td>
                        <td className="text-end">
                          {roundedTotal.toLocaleString("en-IN")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <div className="d-flex justify-content-center gap-2 my-4">
                <Button className="btn btn-secondary" onClick={handleBack}>
                  Back
                </Button>
              </div>
            </div>
          </Card.Body>
        )}
      </Card>
    </Container>
  );
};

export default TemplateView;
